import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export interface ProductStats {
  yearlySalesTotal: number;
  yearlyUnitTotalSoldUnits: number;
}

export interface ProductType {
  _id: string;
  name: string;
  description: string;
  price: number;
  rating: number;
  category: string;
  supply: number;
  stat: ProductStats;
}

export interface MonthlyData {
  month: string;
  totalSales: number;
  totalUnits: number;
}

export interface DailyData {
  date: string;
  totalSales: number;
  totalUnits: number;
}

interface SalesByCategory {
  [category: string]: number;
}

export interface ProductStat {
  productId: string;
  yearlySalesTotal: number;
  yearlyTotalSoldUnits: number;
  year: number;
  monthlyData: MonthlyData[];
  dailyData: DailyData[];
  salesByCategory: SalesByCategory;
}

// typ celé sales response
export type SalesResponse = {
  monthlyData: MonthlyData[];
};

// Blog post types
export interface PostType {
  _id: string;
  title: string;
  summary: string;
  content: string;
  cover: string;
  urlTitle: string;
  author: {
    _id: string;
    email: string;
    username?: string;
  };
  minutes: number;
  // SEO fields
  metaTitle?: string;
  metaDescription?: string;
  keywords?: string[];
  imageAlt?: string;
  // Open Graph
  ogTitle?: string;
  ogDescription?: string;
  // Publishing
  isPublished: boolean;
  publishedAt?: string;
  // Analytics
  views: number;
  // Timestamps
  createdAt: string;
  updatedAt: string;
}

export const api = createApi({
  baseQuery: fetchBaseQuery({ baseUrl: process.env.REACT_APP_BACKEND_URL, credentials: 'include' }),
  reducerPath: 'adminApi',
  tagTypes: [
    'User',
    'Products',
    'Customers',
    'Transactions',
    'Geography',
    'Sales',
    'Admins',
    'Performance',
    'Dashboard',
    'Client',
    'Profile',
    'Devices',
    'DeviceSettings',
    'DeviceShares',
    'Posts',
  ],
  endpoints: (build) => ({
    getUser: build.query({
      query: (id) => `general/user/${id}`,
      providesTags: ['User'],
    }),
    getProducts: build.query<ProductType[], void>({
      query: () => 'client/products',
      providesTags: ['Products'],
    }),
    getCustomers: build.query<any, void>({
      query: () => 'client/customers',
      providesTags: ['Customers'],
    }),
    getTransactions: build.query<any, any>({
      query: ({ page, pageSize, sort, search }) => ({
        url: 'client/transactions',
        method: 'GET',
        params: { page, pageSize, sort, search },
      }),
      providesTags: ['Transactions'],
    }),
    getGeography: build.query<any, void>({
      query: () => 'client/geography',
      providesTags: ['Geography'],
    }),
    getSales: build.query<ProductStat, void>({
      query: () => 'sales/sales',
      providesTags: ['Sales'],
    }),
    getAdmins: build.query<any, void>({
      query: () => 'management/admins',
      providesTags: ['Admins'],
    }),
    getUserPerformance: build.query<any, void>({
      query: (id) => `management/performance/${id}`,
      providesTags: ['Performance'],
    }),
    getDashboard: build.query<any, void>({
      query: () => 'general/dashboard',
      providesTags: ['Dashboard'],
    }),
    getClientId: build.query<any, any>({
      query: (id) => `/client/${id}`,
      providesTags: ['Client'],
    }),
    getProfile: build.query<any, void>({
      query: () => `profile`, // tvůj backend endpoint
      providesTags: ['Profile'],
    }),
    getUserDevices: build.query<any[], string>({
      query: (userId) => `/client/${userId}/devices`,
      providesTags: ['Devices'],
    }),
    assignDeviceToUser: build.mutation<any[], { userId: string; deviceId: string }>({
      query: ({ userId, deviceId }) => ({
        url: `/client/${userId}/devices`,
        method: 'POST',
        body: { deviceId },
      }),
      invalidatesTags: ['Devices'], // po přiřazení se aktualizuje seznam
    }),
    getDeviceSettings: build.query<any, any>({
      query: (deviceId) => `/?deviceId=${deviceId}`,
      providesTags: ['DeviceSettings'],
    }),
    setDeviceSettings: build.mutation<any, any>({
      query: (body) => ({
        url: '/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DeviceSettings'],
    }),
    deleteDeviceSetting: build.mutation<any, any>({
      query: ({ deviceId, scheduleId }) => ({
        url: `/${deviceId}/${scheduleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DeviceSettings'],
    }),
    updateDeviceSetting: build.mutation<any, any>({
      query: ({ deviceId, scheduleId, ...rest }) => ({
        url: `/${deviceId}/${scheduleId}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: ['DeviceSettings'],
    }),
    addWaterBubble: build.mutation<any, { deviceId: string; time: string; duration: number }>({
      query: (body) => ({
        url: '/waterbubble',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['DeviceSettings'],
    }),
    deleteWaterBubble: build.mutation<any, { deviceId: string; bubbleIndex: number }>({
      query: ({ deviceId, bubbleIndex }) => ({
        url: `/waterbubble/${deviceId}/${bubbleIndex}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DeviceSettings'],
    }),
    updateWaterBubble: build.mutation<
      any,
      { deviceId: string; bubbleIndex: number; time?: string; duration?: number }
    >({
      query: ({ deviceId, bubbleIndex, ...rest }) => ({
        url: `/waterbubble/${deviceId}/${bubbleIndex}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: ['DeviceSettings'],
    }),
    updateDeviceDefaultValues: build.mutation<
      any,
      { deviceId: string; humidity?: number; divergence?: number }
    >({
      query: ({ deviceId, ...rest }) => ({
        url: `/defaultValues/${deviceId}`,
        method: 'PUT',
        body: rest,
      }),
      invalidatesTags: ['DeviceSettings'],
    }),
    createDevice: build.mutation<
      any,
      {
        location?: string;
        name?: string;
        defaultHumidity?: number;
        defaultDivergence?: number;
        ipAddress?: string;
        status?: string;
        samplingInterval?: number;
        pins?: {
          relay1?: number;
          relay2?: number;
          relay3?: number;
          ventil?: number;
          humTempSensor?: number;
        };
      }
    >({
      query: (body) => ({
        url: `/create`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Devices', 'DeviceSettings'],
    }),
    // Device sharing endpoints
    shareDevice: build.mutation<
      any,
      { deviceId: string; email: string; accessLevel: 'read' | 'full' }
    >({
      query: (body) => ({
        url: '/api/device-share/share',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Devices', 'DeviceShares'],
    }),
    getDeviceShares: build.query<any[], string>({
      query: (deviceId) => `/api/device-share/${deviceId}/shares`,
      providesTags: ['DeviceShares'],
    }),
    updateDeviceShare: build.mutation<
      any,
      { deviceId: string; shareId: string; accessLevel: 'read' | 'full' }
    >({
      query: ({ deviceId, shareId, accessLevel }) => ({
        url: `/api/device-share/${deviceId}/shares/${shareId}`,
        method: 'PUT',
        body: { accessLevel },
      }),
      invalidatesTags: ['DeviceShares'],
    }),
    removeDeviceShare: build.mutation<void, { deviceId: string; shareId: string }>({
      query: ({ deviceId, shareId }) => ({
        url: `/api/device-share/${deviceId}/shares/${shareId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Devices', 'DeviceShares'],
    }),
    // Schedule history endpoints
    getScheduleHistory: build.query<any[], { deviceId: string; from?: string; to?: string }>({
      query: ({ deviceId, from, to }) => {
        const params = new URLSearchParams();
        if (from) params.append('from', from);
        if (to) params.append('to', to);
        return `/api/schedule-history/${deviceId}${params.toString() ? `?${params}` : ''}`;
      },
      providesTags: ['DeviceSettings'],
    }),
    getEffectiveSchedules: build.query<any[], { deviceId: string; from: string; to: string }>({
      query: ({ deviceId, from, to }) =>
        `/api/schedule-history/${deviceId}/effective?from=${from}&to=${to}`,
      providesTags: ['DeviceSettings'],
    }),
    // Blog posts endpoints
    getPosts: build.query<PostType[], void>({
      query: () => 'post',
      providesTags: ['Posts'],
    }),
    getPost: build.query<PostType, string>({
      query: (urlTitle) => `post/${urlTitle}`,
      providesTags: ['Posts'],
    }),
    deletePost: build.mutation<void, string>({
      query: (id) => ({
        url: `post/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Posts'],
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetProductsQuery,
  useGetCustomersQuery,
  useGetTransactionsQuery,
  useGetGeographyQuery,
  useGetSalesQuery,
  useGetAdminsQuery,
  useGetUserPerformanceQuery,
  useGetDashboardQuery,
  useGetClientIdQuery,
  useGetProfileQuery,
  useGetUserDevicesQuery,
  useAssignDeviceToUserMutation,
  useGetDeviceSettingsQuery,
  useSetDeviceSettingsMutation,
  useDeleteDeviceSettingMutation,
  useUpdateDeviceSettingMutation,
  useAddWaterBubbleMutation,
  useDeleteWaterBubbleMutation,
  useUpdateWaterBubbleMutation,
  useUpdateDeviceDefaultValuesMutation,
  useCreateDeviceMutation,
  useShareDeviceMutation,
  useGetDeviceSharesQuery,
  useUpdateDeviceShareMutation,
  useRemoveDeviceShareMutation,
  useGetScheduleHistoryQuery,
  useGetEffectiveSchedulesQuery,
  useGetPostsQuery,
  useGetPostQuery,
  useDeletePostMutation,
} = api;
